import { AspectRatio, Image, Text, Button, Stack } from '@mantine/core'
import classes from './ContentPage.module.css'
import {
  IconChevronLeft,
  IconExternalLink,
  IconDownload,
} from '@tabler/icons-react'
import { useLocation, useNavigate, useParams } from 'react-router'
import Header from '../common/components/header/Header'
import DOMPurify from 'dompurify'
import clsx from 'clsx'
import Meta from '../common/Meta'
import { useMediaQuery } from '@mantine/hooks'
import { Typography } from '../common/typography'
import { ContentPageSection, useContentPageDetails } from './api'
import scroll from '../common/components/scroll/Scroll.module.css'

// TODO: clean up styles, Hrvoje just re-used them from news

export default function ContentPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isDesktop = useMediaQuery('(min-width: 800px)')

  if (!slug) {
    navigate('/news')
    return null
  }

  const { data: pageData } = useContentPageDetails({ slug })

  return (
    <div className={classes.container}>
      {isDesktop && (
        <IconChevronLeft
          onClick={() => navigate(location.state?.from?.pathname || '/news')}
          className={classes.backIcon}
        />
      )}
      <Header
        title={pageData.title}
        back={location.state?.from?.pathname || '/news'}
      />
      <Meta title={`Page | ${pageData.title}`} />
      <div className={clsx(classes.newsDetailsContainer, scroll.scroll)}>
        <div className={classes.newsDetailsMedia}>
          <div className={classes.newsDetailsCard}>
            <div className={classes.newsDetailsDescription}>
              <Typography level={2} fs="xxxl" fw={700} lh="125">
                {pageData.title}
              </Typography>
            </div>
            {pageData.content && (
              <div className={classes.newsDetailsBody}>
                <Text
                  component="div"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(pageData.content),
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <Stack gap="md">
          {pageData.sections.map((section, index) => (
            <ContentPageSectionView
              key={`section-${index}`}
              section={section}
            />
          ))}
        </Stack>
      </div>
    </div>
  )
}

export function ContentPageSectionView({
  section,
}: {
  section: ContentPageSection
}) {
  return (
    <div className={classes.newsDetailsMedia}>
      <div className={classes.newsDetailsCard}>
        <Stack gap="md">
          {/* Section Title */}
          <Typography level={3} fs="xl" fw={600}>
            {section.title}
          </Typography>

          {/* Section Image */}
          {section.image && section.image.main && (
            <AspectRatio ratio={16 / 9}>
              <Image
                src={section.image.main}
                alt={section.title}
                radius="md"
                fit="cover"
              />
            </AspectRatio>
          )}

          {/* Section Content */}
          {section.content && (
            <div className={classes.newsDetailsBody}>
              <Text
                component="div"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(section.content),
                }}
              />
            </div>
          )}

          {/* Action Buttons */}
          <Stack gap="sm">
            {/* Document Download */}
            {section.document && section.document.main && (
              <Button
                component="a"
                href={section.document.main}
                download
                leftSection={
                  <IconDownload className={classes.externalButtonIcon} />
                }
                variant="light"
                fullWidth={false}
                className={classes.externalButton}
              >
                <Typography
                  fw={500}
                  fc={{ light: 'white', dark: 'white' }}
                  lh="88"
                  tt="uppercase"
                >
                  Download Document
                </Typography>
              </Button>
            )}

            {/* External Link */}
            {section.external_link && (
              <Button
                component="a"
                href={section.external_link}
                target="_blank"
                rel="noopener noreferrer"
                leftSection={
                  <IconExternalLink className={classes.externalButtonIcon} />
                }
                variant="light"
                fullWidth={false}
                className={classes.externalButton}
              >
                <Typography
                  fw={500}
                  fc={{ light: 'white', dark: 'white' }}
                  lh="88"
                  tt="uppercase"
                >
                  View External Resource
                </Typography>
              </Button>
            )}
          </Stack>
        </Stack>
      </div>
    </div>
  )
}
